import { ToastService } from "@shared/lib/services";
import {
  IReportCustomFieledConfig,
  ReportMedia,
  ReportType,
} from "@shared/lib/types/reports";
import { formatData } from "@shared/lib/utils/formatData";
import { Avatar, Button, Popconfirm, Table, Tag } from "@shared/ui";
import { Close } from "@shared/ui/icons";
import InputNumber from "@shared/ui/InputNumber";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { ReportMediaDrawer } from "@widgets/ReportMediaDrawer";
import { Flex } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import { Check, ExternalLink, Instagram, Trash2, Youtube } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  buildReportCustomFieldColumns,
  type ReportDataFieldEditState,
} from "./buildReportCustomFieldColumns";
import styles from "./ReportMediaTable.module.scss";

type ReportMediaTableProps = {
  reportMedia: ReportMedia[];
  isFetching: boolean;
  pageNo: number;
  pageSize: number;
  totaldata: number;
  onPageChange: (page: number) => void;
  totalPages: number;
  reportName: string;
  reportId: string;
  onEditCost: (reportMediaId: string, cost: number) => void;
  isEditing: boolean;
  isEdited: boolean;
  visibleCustomFields?: IReportCustomFieledConfig[];
  dataFieldEditProps?: ReportDataFieldEditState;
  setDataFieldEditProps?: React.Dispatch<
    React.SetStateAction<ReportDataFieldEditState>
  >;
  onSaveReportDataField?: (payload: {
    itemId: string;
    dataField: { [key: string]: string | number };
  }) => void;
  isSavingDataField?: boolean;
  onDeleteMedia?: (mediaId: string) => void;
  isDeletingMedia?: boolean;
  deletingMediaId?: string;
  isDeleteMediaDisabled?: boolean;
  reportType: ReportType;
};

export function ReportMediaTable({
  reportMedia,
  isFetching,
  pageNo,
  pageSize,
  totaldata,
  onPageChange,
  totalPages,
  reportName,
  reportId,
  onEditCost,
  isEditing,
  isEdited,
  visibleCustomFields = [],
  dataFieldEditProps,
  setDataFieldEditProps,
  onSaveReportDataField,
  isSavingDataField = false,
  onDeleteMedia,
  isDeletingMedia = false,
  deletingMediaId,
  isDeleteMediaDisabled = false,
  reportType,
}: ReportMediaTableProps) {
  const [openDrawerProps, setOpenDrawerProps] = useState<{
    open: boolean;
    mediaId: string;
    reportId: string;
  } | null>(null);
  const [editingCost, setEditingCost] = useState<{
    reportMediaId: string;
    cost?: number;
  } | null>(null);

  useEffect(() => {
    if (isEdited) {
      setEditingCost(null);
      ToastService.success("Cost updated successfully");
    }
  }, [isEdited]);

  const customFieldColumns = useMemo(
    () =>
      visibleCustomFields.length > 0 &&
      setDataFieldEditProps &&
      onSaveReportDataField
        ? buildReportCustomFieldColumns({
            fields: visibleCustomFields,
            editFieldProps: dataFieldEditProps ?? {
              reportMediaId: "",
              key: "",
              value: undefined,
            },
            setEditFieldProps: setDataFieldEditProps,
            onSave: onSaveReportDataField,
            isSaving: isSavingDataField,
          })
        : [],
    [
      visibleCustomFields,
      dataFieldEditProps,
      setDataFieldEditProps,
      onSaveReportDataField,
      isSavingDataField,
    ]
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Success":
        return "green";
      case "Pending":
        return "blue";
      case "Failed":
        return "red";
      default:
        return "default";
    }
  };

  const columns: ColumnsType<ReportMedia> = [
    {
      title: "Creator",
      dataIndex: "createdBy",
      key: "creator",
      width: 200,
      render: (_value, record: ReportMedia) => {
        const platform = record.platform?.toLowerCase();
        const creatorName = record.socialMedia?.searchV1?.fullName || "";
        const handle = `@${record.socialMedia?.username || " "}`;

        return (
          <div className={styles.creatorCell}>
            <div className={styles.avatarContainer}>
              <Avatar
                size={36}
                src={record.socialMedia?.profilePicUrl}
                className={styles.creatorAvatar}
              >
                {creatorName[0]}
              </Avatar>
              {platform && (
                <div className={styles.platformBadge}>
                  {platform === "instagram" ? (
                    <Instagram
                      size={10}
                      className={styles.platformIconInstagram}
                    />
                  ) : platform === "youtube" ? (
                    <Youtube size={10} className={styles.platformIconYoutube} />
                  ) : platform === "tiktok" ? (
                    <div className={styles.tiktokBadge}>T</div>
                  ) : null}
                </div>
              )}
            </div>
            <div className={styles.creatorMeta}>
              {creatorName && (
                <>
                  <TextWithTooltip
                    text={creatorName}
                    limit={18}
                    textClassName={styles.creatorName}
                  />
                  <div className={styles.creatorHandle}>{handle}</div>
                </>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "mediaType",
      key: "type",
      render: (_value, record: ReportMedia) => {
        const platform = record.platform?.toLowerCase();
        const mediaType = record.mediaType || "";

        const getTypeIcon = () => {
          if (platform === "instagram") {
            return <Instagram size={12} />;
          }
          if (platform === "youtube") {
            return <Youtube size={12} />;
          }
          return null;
        };

        return (
          <span className={styles.typeBadge}>
            {getTypeIcon()}
            {mediaType}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "mediaDataStatus",
      key: "mediaDataStatus",
      render: (_value, record: ReportMedia) => {
        const status = record.mediaDataStatus || "Pending";
        return <Tag color={getStatusColor(status)}>{status}</Tag>;
      },
    },
    {
      title: "Views",
      dataIndex: "mediaData.views",
      key: "views",
      render: (_value, record: ReportMedia) => {
        const views = record.mediaData?.views || 0;
        const status = record.mediaDataStatus || "Pending";
        if (status === "Pending") {
          return null;
        }
        return <div className={styles.viewsValue}>{formatData(views)}</div>;
      },
    },
    {
      title: "Eng. Rate",
      dataIndex: "mediaData.engRate",
      key: "engRate",
      render: (_value, record: ReportMedia) => {
        const status = record.mediaDataStatus || "Pending";
        if (status === "Pending") {
          return null;
        }
        const engRate = record.mediaData?.engRate || 0;
        return (
          <div className={styles.engRateBadge}>{formatData(engRate)}%</div>
        );
      },
    },
    {
      title: "Likes",
      dataIndex: "mediaData.likes",
      key: "likes",
      render: (_value, record: ReportMedia) => {
        const status = record.mediaDataStatus || "Pending";
        if (status === "Pending") {
          return null;
        }
        const likes = record.mediaData?.likes || 0;
        return <div className={styles.likesValue}>{formatData(likes)}</div>;
      },
    },
    {
      title: "Comments",
      dataIndex: "mediaData.comments",
      key: "comments",
      render: (_value, record: ReportMedia) => {
        const status = record.mediaDataStatus || "Pending";
        if (status === "Pending") {
          return null;
        }
        const comments = record.mediaData?.comments || 0;
        return (
          <div className={styles.commentsCell}>
            <span className={styles.commentsTotal}>{formatData(comments)}</span>
          </div>
        );
      },
    },
    {
      title: "Shares",
      dataIndex: "mediaData.shares",
      key: "shares",
      render: (_value, record: ReportMedia) => {
        const status = record.mediaDataStatus || "Pending";
        if (status === "Pending") {
          return null;
        }
        const shares = record.mediaData?.shares || 0;
        return <div className={styles.sharesValue}>{formatData(shares)}</div>;
      },
    },
    {
      title: "Commercial's",
      dataIndex: "cost",
      key: "cost",
      render: (_value, record: ReportMedia) => {
        const status = record.mediaDataStatus || "Pending";
        if (status === "Pending") {
          return null;
        }
        const cost = record.cost || 0;
        const isEditingField =
          editingCost !== null && editingCost?.reportMediaId === record._id;

        if (isEditingField) {
          const handleSave = () => {
            const costToSave = editingCost?.cost || 0;
            if (costToSave > 0) {
              onEditCost(record._id, costToSave);
            }
          };

          const handleCancel = () => {
            setEditingCost(null);
          };

          const suffixActions = (
            <Flex gap={2}>
              <Button
                icon={<Check size={10} />}
                type="text"
                size="small"
                loading={isEditing}
                onClick={handleSave}
                disabled={isEditing || (editingCost?.cost || 0) <= 0}
                className={styles.editActionButton}
              />
              <Button
                icon={<Close />}
                type="text"
                size="small"
                onClick={handleCancel}
                className={styles.editActionButton}
              />
            </Flex>
          );

          return (
            <InputNumber
              value={Number(editingCost?.cost || 0)}
              onChange={(val) => {
                setEditingCost({
                  reportMediaId: record._id,
                  cost: Number(val ?? 0),
                });
              }}
              onPressEnter={handleSave}
              size="small"
              className={styles.customFieldInput}
              addonAfter={suffixActions}
              disabled={isEditing}
              autoFocus
            />
          );
        }

        return (
          <div
            className={styles.costValue}
            onClick={(e) => {
              e.stopPropagation();
              setEditingCost({
                reportMediaId: record._id,
                cost,
              });
            }}
          >
            {cost >= 0 ? `₹${formatData(cost, "0.00")}` : "-"}
          </div>
        );
      },
    },
    ...customFieldColumns,
    {
      title: "CPV",
      dataIndex: "cpv",
      key: "cpv",
      render: (_value, record: ReportMedia) => {
        const status = record.mediaDataStatus || "Pending";
        if (status === "Pending") {
          return null;
        }
        const cpv = record.cost
          ? record.cost / (record.mediaData?.views || 1)
          : 0;
        return (
          <div className={styles.cpvValue}>
            {cpv > 0 ? `₹${formatData(cpv, "0.00")}` : "-"}
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 150,
      align: "right",
      render: (_value, record: ReportMedia) => {
        const status = record.mediaDataStatus || "Pending";
        const isDeletingThisRow =
          isDeletingMedia && deletingMediaId === record._id;

        return (
          <div className={styles.actionsCell}>
            <Button
              type="link"
              size="small"
              className={styles.viewButton}
              onClick={(e) => {
                e.stopPropagation();
                setOpenDrawerProps({
                  open: true,
                  mediaId: record._id,
                  reportId: record.reportId || reportId,
                });
              }}
            >
              View Detailed
            </Button>
            {status !== "Pending" && (
              <>
                <a
                  href={record.link}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className={styles.externalLink}
                >
                  <ExternalLink size={14} />
                </a>
                {onDeleteMedia && reportType !== ReportType.Brief && (
                  <Popconfirm
                    title="Remove content"
                    description="Are you sure you want to remove this content from the report?"
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      onDeleteMedia(record._id);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                    okText="Remove"
                    cancelText="Cancel"
                    okButtonProps={{
                      danger: true,
                      loading: isDeletingThisRow,
                    }}
                    disabled={isDeleteMediaDisabled || isDeletingMedia}
                  >
                    <Button
                      type="text"
                      size="small"
                      className={styles.deleteButton}
                      icon={<Trash2 size={14} />}
                      loading={isDeletingThisRow}
                      disabled={isDeleteMediaDisabled || isDeletingMedia}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>
                )}
              </>
            )}
          </div>
        );
      },
    },
  ];

  const handleTableChange: TableProps<ReportMedia>["onChange"] = (
    pagination
  ) => {
    if (pagination?.current) {
      onPageChange(pagination.current);
    }
  };

  return (
    <div className={styles.tableWrapper}>
      <Table<ReportMedia>
        columns={columns}
        title={() => (
          <div className={styles.tableTitle}>
            <div className={styles.titleText}>
              Content Report · {reportName}
            </div>
          </div>
        )}
        dataSource={reportMedia}
        loading={isFetching}
        pagination={{
          current: pageNo,
          pageSize,
          total: totaldata,
          showSizeChanger: false,
          // showTotal: (total) => (
          //   <span className={styles.paginationText}>
          //     Showing {total} content items
          //   </span>
          // ),
          className: styles.paginationBar,
          itemRender: (_page, type, originalElement) => {
            if (type === "prev") {
              return (
                <Button
                  size="small"
                  className={styles.paginationNav}
                  disabled={pageNo <= 1}
                  onClick={() => {
                    if (pageNo > 1) {
                      onPageChange(pageNo - 1);
                    }
                  }}
                >
                  Previous
                </Button>
              );
            }
            if (type === "next") {
              return (
                <Button
                  size="small"
                  disabled={pageNo >= totalPages}
                  className={styles.paginationNav}
                  onClick={() => {
                    if (pageNo < totalPages) {
                      onPageChange(pageNo + 1);
                    }
                  }}
                >
                  Next
                </Button>
              );
            }
            return originalElement;
          },
        }}
        onChange={handleTableChange}
        rowKey="_id"
        rowClassName={() => styles.tableRow}
        scroll={{ x: true }}
        locale={{
          emptyText: (
            <div className={styles.emptyState}>
              No content found in this report.
            </div>
          ),
        }}
        className={styles.contentTable}
      />
      <ReportMediaDrawer
        open={openDrawerProps?.open || false}
        onClose={() => setOpenDrawerProps(null)}
        mediaId={openDrawerProps?.mediaId || ""}
        reportId={openDrawerProps?.reportId || ""}
        reportName={reportName}
      />
    </div>
  );
}
